import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { StaffPermissionKey } from "@/lib/staff-permissions";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isAdmin: boolean;
  isStaff: boolean;
  staffId: string | null;
  staffStatus: string | null;
  staffRole: string | null;
  staffPermissions: Record<string, boolean>;
  can: (perm: StaffPermissionKey) => boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  loading: true,
  isAdmin: false,
  isStaff: false,
  staffId: null,
  staffStatus: null,
  staffRole: null,
  staffPermissions: {},
  can: () => false,
  signOut: async () => {},
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isStaff, setIsStaff] = useState(false);
  const [staffId, setStaffId] = useState<string | null>(null);
  const [staffStatus, setStaffStatus] = useState<string | null>(null);
  const [staffRole, setStaffRole] = useState<string | null>(null);
  const [staffPermissions, setStaffPermissions] = useState<Record<string, boolean>>({});

  const checkRoles = async (userId: string) => {
    // Check admin
    const { data: adminData } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", userId)
      .eq("role", "admin")
      .maybeSingle();
    setIsAdmin(!!adminData);

    // Check if this user is a staff member
    const { data: staffData } = await supabase
      .from("staff")
      .select("id, is_active, access_status, access_role, permissions")
      .eq("user_id", userId)
      .maybeSingle();

    const approved = !!staffData && staffData.is_active && staffData.access_status === "approved";
    setIsStaff(approved);
    setStaffId(approved ? staffData!.id : null);
    setStaffStatus(staffData ? (staffData.access_status as string) : null);
    setStaffRole(staffData ? (staffData.access_role as string) : null);
    setStaffPermissions(
      approved ? ((staffData!.permissions as Record<string, boolean>) || {}) : {}
    );
  };

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        if (session?.user) {
          setTimeout(() => checkRoles(session.user.id), 0);
        } else {
          setIsAdmin(false);
          setIsStaff(false);
          setStaffId(null);
          setStaffStatus(null);
          setStaffRole(null);
          setStaffPermissions({});
        }
        setLoading(false);
      }
    );

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) checkRoles(session.user.id);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
    setIsAdmin(false);
    setIsStaff(false);
    setStaffId(null);
    setStaffStatus(null);
    setStaffRole(null);
    setStaffPermissions({});
  };

  const can = (perm: StaffPermissionKey) => isAdmin || staffPermissions[perm] === true;

  return (
    <AuthContext.Provider value={{ user, session, loading, isAdmin, isStaff, staffId, staffStatus, staffRole, staffPermissions, can, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};
